import React, { useEffect, useState } from "react";
import { functionsService } from "@/services/functionsService";
import { Card } from "@/components/ui/Card";

interface KeyMetricsProps {
  ticker: string;
}

export const KeyMetrics: React.FC<KeyMetricsProps> = ({ ticker }) => {
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, [ticker]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const result = await functionsService.getKeyMetrics(ticker, 1);
      if (result.success && result.metrics.length > 0) {
        setMetrics(result.metrics);
      }
    } catch (error) {
      console.error("Failed to load metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (metrics.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="sub-header text-xl mb-2">Key Financial Metrics</h3>
        <p className="body-text text-gray-600 mb-4">
          Key metrics could not be loaded for this ticker. The financial data API may not have data, or the ticker may be invalid.
        </p>
        <button
          type="button"
          onClick={loadMetrics}
          className="text-blazerex-green hover:underline font-medium"
        >
          Try again
        </button>
      </Card>
    );
  }

  const latest = metrics[0];

  const metricGroups = [
    {
      title: "Valuation",
      metrics: [
        { label: "P/E Ratio", value: latest.peRatio },
        { label: "P/B Ratio", value: latest.pbRatio },
        { label: "Price to Sales", value: latest.priceToSalesRatio },
        { label: "Enterprise Value", value: latest.enterpriseValue },
      ],
    },
    {
      title: "Profitability",
      metrics: [
        { label: "ROE", value: latest.roe },
        { label: "ROA", value: latest.roa },
        { label: "ROIC", value: latest.roic },
        { label: "Profit Margin", value: latest.netIncomeRatio },
      ],
    },
    {
      title: "Financial Health",
      metrics: [
        { label: "Debt to Equity", value: latest.debtToEquity },
        { label: "Current Ratio", value: latest.currentRatio },
        { label: "Interest Coverage", value: latest.interestCoverage },
        { label: "Debt to Assets", value: latest.debtToAssets },
      ],
    },
    {
      title: "Dividends",
      metrics: [
        { label: "Dividend Yield", value: latest.dividendYield },
        { label: "Payout Ratio", value: latest.payoutRatio },
      ],
    },
  ];

  const formatValue = (value: number | undefined | null) => {
    if (value === undefined || value === null) return "N/A";
    if (typeof value === "number") {
      if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
      if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
      return value.toFixed(2);
    }
    return String(value);
  };

  return (
    <Card className="p-6">
      <h3 className="sub-header text-xl mb-6">Key Financial Metrics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {metricGroups.map((group) => (
          <div key={group.title}>
            <h4 className="body-text font-header-bold text-gray-700 mb-3">
              {group.title}
            </h4>
            <div className="space-y-2">
              {group.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span className="body-text text-gray-600">{metric.label}</span>
                  <span className="body-text font-header-bold">
                    {formatValue(metric.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

